import type { ApiError } from './types';

export class ApiClientError extends Error {
  constructor(
    public status: number,
    public detail: string,
    /** The raw `detail` when the server sent a structured one. A 409 that
     * says *what else you could do* is useless if it arrives as
     * "[object Object]" — callers read this to offer the next step. */
    public data?: unknown,
  ) {
    super(detail);
    this.name = 'ApiClientError';
  }
}

export interface ClientOptions {
  baseUrl: string;
  /** App identifier (rep|ops|acct) sent as X-Client-App on every request */
  app?: string;
  onUnauthorized?: () => void;
}

export function createClient(options: ClientOptions) {
  const { baseUrl, app, onUnauthorized } = options;
  const appHeader: Record<string, string> = app ? { 'X-Client-App': app } : {};

  async function request<T>(
    path: string,
    init: RequestInit = {},
  ): Promise<T> {
    const url = `${baseUrl}${path}`;
    const res = await fetch(url, {
      ...init,
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        ...appHeader,
        ...init.headers,
      },
    });

    if (res.status === 401) {
      onUnauthorized?.();
      throw new ApiClientError(401, 'Unauthorized');
    }

    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as ApiError;
      const detail = body.detail;
      const message = typeof detail === 'string'
        ? detail
        : (detail && typeof detail === 'object' && 'message' in detail
            ? String((detail as { message: unknown }).message)
            : res.statusText);
      throw new ApiClientError(res.status, message, detail);
    }

    if (res.status === 204) return undefined as T;
    return res.json() as Promise<T>;
  }

  return {
    /** Which app this client belongs to (rep|ops|acct).
     *
     * A browser NAVIGATION — <a href> download, window.open — cannot carry the
     * X-Client-App header, so the server falls back to trying every session
     * cookie in order. A browser signed into more than one app then
     * authenticates as whichever comes first, not as the app you are using.
     * URL builders append `?app=` so a link identifies its app the same way a
     * fetch does. */
    app,

    get: <T>(path: string) => request<T>(path),

    post: <T>(path: string, body?: unknown) =>
      request<T>(path, {
        method: 'POST',
        body: body ? JSON.stringify(body) : undefined,
      }),

    put: <T>(path: string, body?: unknown) =>
      request<T>(path, {
        method: 'PUT',
        body: body ? JSON.stringify(body) : undefined,
      }),

    del: <T>(path: string, body?: unknown) =>
      request<T>(path, {
        method: 'DELETE',
        body: body ? JSON.stringify(body) : undefined,
      }),

    /** Raw fetch for non-JSON responses (e.g. file downloads) */
    raw: (path: string, init: RequestInit = {}) =>
      fetch(`${baseUrl}${path}`, {
        ...init,
        credentials: 'same-origin',
        headers: {
          ...appHeader,
          ...init.headers,
        },
      }),
  };
}

export type Client = ReturnType<typeof createClient>;
